// useScrollSpy(ids, { enabled?, prefer? }) → the id of the current figure head (DESIGN_SPEC §10.6, must-fix 16)
// The band under the sticky chrome (nav + figure index + 8px) down to 40% of the viewport is watched
// with an IntersectionObserver (rootMargin -(nav+bar+8)px 0 -60% 0): the topmost head inside the band
// wins; with none inside, the last head above it; at the very bottom of the page, the last head that is
// on screen. Heads side by side in one journal row share a top: `prefer` (the figure the reader just
// jumped to) wins that tie. A passive scroll listener covers the page end, where heads stop crossing the band.
//   stepFrom(ids, current, d) → the head j / k go to: the next or previous one, but a current head the reader has not
//   reached yet (it still sits below the band, e.g. from the page header) comes first.
import { useEffect, useRef, useState } from "react";

function px(name: string, fallback: number): number {
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
}

const bandTop = () => px("--nav-h", 64) + px("--bar-h", 46) + 8;

export function stepFrom(ids: readonly string[], current: string | null, d: 1 | -1): string | undefined {
  if (!ids.length) return undefined;
  const i = Math.max(0, current ? ids.indexOf(current) : 0);
  const el = document.getElementById(ids[i]);
  const doc = document.documentElement;
  const atEnd = window.innerHeight + window.scrollY >= doc.scrollHeight - 2;
  const ahead = d > 0 && !atEnd && !!el && el.getBoundingClientRect().top > bandTop() + 24;
  return ids[Math.min(ids.length - 1, Math.max(0, ahead ? i : i + d))];
}

export function useScrollSpy(ids: readonly string[], opts: { enabled?: boolean; prefer?: string | null } = {}): string | null {
  const { enabled = true, prefer = null } = opts;
  const [current, setCurrent] = useState<string | null>(ids[0] ?? null);
  const preferRef = useRef(prefer);
  const recompute = useRef<() => void>(() => undefined);
  const key = ids.join("|");

  useEffect(() => {
    preferRef.current = prefer;
    recompute.current();
  }, [prefer]);

  useEffect(() => {
    if (!enabled) return;
    const list = key ? key.split("|") : [];
    const els = list.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el);
    if (!els.length) return;
    let frame = 0;
    const top = bandTop;

    const compute = () => {
      frame = 0;
      const t = top();
      const band = t + (window.innerHeight - t) * 0.4;
      let inBand: HTMLElement | null = null;
      let above: HTMLElement | null = null;
      let lastVisible: HTMLElement | null = null;
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.top >= t - 1 && r.top <= band) inBand ??= el;
        else if (r.top < t - 1) above = el;
        if (r.top < window.innerHeight && r.bottom > t) lastVisible = el;
      }
      const doc = document.documentElement;
      const atEnd = window.innerHeight + window.scrollY >= doc.scrollHeight - 2 && window.scrollY > 0;
      const pick = (atEnd ? lastVisible : null) ?? inBand ?? above ?? els[0];
      const pref = preferRef.current ? els.find((el) => el.id === preferRef.current) : undefined;
      const tie = pref && Math.abs(pref.getBoundingClientRect().top - pick.getBoundingClientRect().top) < 2;
      setCurrent(tie ? pref.id : pick.id);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(compute);
    };
    recompute.current = schedule;

    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver === "function") {
      io = new IntersectionObserver(schedule, { rootMargin: `-${Math.round(top())}px 0px -60% 0px`, threshold: [0, 1] });
      els.forEach((el) => io?.observe(el));
    }
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    compute();
    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) cancelAnimationFrame(frame);
      recompute.current = () => undefined;
    };
  }, [key, enabled]);

  return enabled ? current : null;
}
